// composables/useMediaMTXWebRTC.ts
import {
  ref,
  watch,
  onUnmounted,
  type Ref,
  type MaybeRefOrGetter,
  toValue,
} from 'vue'

import { useVideoFrame } from './useVideoFrame'

interface UseMediaMTXWebRTCOptions {
  url: MaybeRefOrGetter<string>
  videoEl: Ref<HTMLVideoElement | null>
  user?: string
  pass?: string
  token?: string
  onError?: (err: string) => void
  autoConnect?: boolean

  // Retry options
  maxRetries?: number // default: 15
  retryDelay?: number // initial delay in ms (default: 1500)
  maxRetryDelay?: number // max delay (default: 8000)
}

export function useMediaMTXWebRTC (options: UseMediaMTXWebRTCOptions) {
  const {
    videoEl,
    user,
    pass,
    token,
    onError,
    autoConnect = false,
    maxRetries = 15,
    retryDelay = 1500,
    maxRetryDelay = 8000,
  } = options

  let pc: RTCPeerConnection | null = null
  let sessionUrl: string | null = null
  let retryCount = 0
  let retryTimer: ReturnType<typeof setTimeout> | null = null
  let isManualDisconnect = false

  const isConnected = ref(false)
  const isConnecting = ref(false)
  const error = ref<string | null>(null)
  const currentUrl = ref('')

  const { addFrame } = useVideoFrame()

  function clearRetry () {
    if (retryTimer) {
      clearTimeout(retryTimer)
      retryTimer = null
    }
  }

  async function disconnect () {
    isManualDisconnect = true
    clearRetry()
    retryCount = 0

    if (pc) {
      pc.close()
      pc = null
    }

    if (sessionUrl) {
      try {
        await fetch(sessionUrl, { method: 'DELETE' })
      } catch (_) {}
      sessionUrl = null
    }

    if (videoEl.value) {
      videoEl.value.srcObject = null
    }

    isConnected.value = false
    isConnecting.value = false
  }

  async function connect (newUrl?: string, videoUrl?:string) {
    const targetUrl = newUrl ?? toValue(options.url)

    if (!targetUrl) {
      error.value = 'No URL provided'
      onError?.(error.value)
      return
    }

    // Stop any previous connection / retry loop
    await disconnect()
    isManualDisconnect = false

    currentUrl.value = targetUrl
    isConnecting.value = true
    error.value = null

    const attempt = async (): Promise<void> => {
      if (isManualDisconnect) return

      try {
        // const iceServers = [{ urls: 'stun:stun.l.google.com:19302' }]
        pc = new RTCPeerConnection()

        pc.addTransceiver('video', { direction: 'recvonly' })
        pc.addTransceiver('audio', { direction: 'recvonly' })

        pc.ontrack = (evt) => {
          if (videoEl.value && evt.streams[0]) {
            videoEl.value.srcObject = evt.streams[0]
            isConnected.value = true
            isConnecting.value = false
            retryCount = 0
          }
        }

        pc.onconnectionstatechange = () => {
          if (
            pc?.connectionState === 'failed' ||
            pc?.connectionState === 'disconnected'
          ) {
            error.value = `Connection ${pc.connectionState}`
            onError?.(error.value)
            isConnected.value = false
          }
        }

        const offer = await pc.createOffer()
        await pc.setLocalDescription(offer)

        const headers: Record<string, string> = {
          'Content-Type': 'application/sdp',
        }

        if (token) {
          headers.Authorization = `Bearer ${token}`
        } else if (user && pass) {
          headers.Authorization = 'Basic ' + btoa(`${user}:${pass}`)
        }

        const res = await fetch(targetUrl, {
          method: 'POST',
          headers,
          body: offer.sdp,
        })

        // ===== Retry on 404 (stream not ready yet) =====
        if (res.status === 404) {
          throw new Error('404')
        }

        if (!res.ok) {
          throw new Error(`WHEP failed: ${res.status} ${res.statusText}`)
        }

        sessionUrl = res.headers.get('Location')
        const answerSdp = await res.text()

        await pc.setRemoteDescription({
          type: 'answer',
          sdp: answerSdp,
        })

        if (videoUrl) {
          addFrame(videoUrl)
        }
        // Success
        retryCount = 0
      } catch (err: any) {
        // Clean up failed peer connection
        if (pc) {
          pc.close()
          pc = null
        }

        const is404 = err.message === '404' || err.message?.includes('404')

        if (is404 && retryCount < maxRetries && !isManualDisconnect) {
          retryCount++

          // Exponential backoff
          const delay = Math.min(
            retryDelay * Math.pow(1.4, retryCount - 1),
            maxRetryDelay
          )

          error.value = `Stream not ready (404). Retry ${retryCount}/${maxRetries} in ${Math.round(delay)}ms...`
          console.warn(error.value)

          retryTimer = setTimeout(() => {
            attempt()
          }, delay)

          return
        }

        // Final failure
        error.value = is404
          ? `Stream not found after ${maxRetries} retries`
          : err.message || String(err)

        onError?.(error.value)
        isConnecting.value = false
        isConnected.value = false
        console.error(err)
      }
    }

    await attempt()
  }

  // Auto connect when url changes
  if (autoConnect) {
    watch(
      () => toValue(options.url),
      (newUrl) => {
        if (newUrl) connect(newUrl)
      },
      { immediate: true }
    )
  }

  onUnmounted(() => {
    disconnect()
  })

  return {
    connect,
    disconnect,
    isConnected,
    isConnecting,
    error,
    currentUrl,
  }
}
